import { useTranslations } from 'next-intl';
import { BaseLineCancelsubmission } from '@/app/components/icons/BaseLineCancelsubmission';
import { BaseLineCopy } from '@/app/components/icons/BaseLineCopy';
import { BaseLineEdit } from '@/app/components/icons/BaseLineEdit';
import { BaseLineSubmit3 } from '@/app/components/icons/BaseLineSubmit3';
import { CollectBtn } from '@/app/components/shared/Community';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import LikeButton from '@/app/components/shared/LikeButton';
import { Button } from '@/app/components/ui/button';
import type { ReferenceModalState } from '@/app/stores/slices/reference-modal';
import { CommunityEnum, type UpdateInteractionStatusFn } from '@/app/types';
import { cn } from '@/app/utils';
import type { TitleSectionMode } from './TitleSection';

interface FooterBtnGroupProps {
  className?: string;
  isTeamSpace?: boolean;
  mode: TitleSectionMode;
  isAuthor: boolean;
  isSubmitSuccess: boolean;
  disableSubmit?: boolean;
  isSubmitForbidden?: boolean;
  shareElementId?: string;
  reactInfo?: {
    voted?: boolean;
    vote_count?: number;
    collected?: boolean;
    collect_count?: number;
  };
  from?: ReferenceModalState['referenceModalFrom'];
  allowCopy?: boolean;
  referenceModalType?: 'share' | 'reference' | null;
  elementType?: 'unspecified' | 'preset' | 'user';
  onCancel: () => void;
  onSubmit: () => void;
  onCopy: () => void;
  onUse: () => void;
  onEdit?: () => void;
  onSubmitToCommunity?: () => void;
  updateVote: UpdateInteractionStatusFn;
  updateUnVote: UpdateInteractionStatusFn;
  onCancelSelect: () => void;
  handleCollect: UpdateInteractionStatusFn;
  handleUnCollect: UpdateInteractionStatusFn;
  canCollect?: boolean;
  handleClose: () => void;
  // 是否是商用授权
  isCommercial: boolean;
}

export const FooterBtnGroup = ({
  className,
  isTeamSpace = false,
  mode,
  isAuthor,
  isSubmitSuccess,
  disableSubmit,
  isSubmitForbidden = false,
  shareElementId,
  reactInfo,
  from,
  referenceModalType,
  elementType,
  onCancel,
  onSubmit,
  onCopy,
  onUse,
  onEdit,
  onSubmitToCommunity,
  updateVote,
  updateUnVote,
  onCancelSelect,
  handleCollect,
  handleUnCollect,
  canCollect,
  handleClose,
  isCommercial,
}: FooterBtnGroupProps) => {
  const t = useTranslations();

  // 判断条件
  const isPreset = elementType === 'preset';
  const isPersonalUnsubmitted = referenceModalType === 'reference' && !shareElementId && isAuthor;
  const isShareAuthor = referenceModalType === 'share' && isAuthor;
  const isSharedApprovedAuthor = isShareAuthor && isSubmitSuccess;
  const isSharedNotAuthor = referenceModalType === 'share' && !isAuthor;
  const isFromSelect = ['select', 'character2video', 'reference2image'].includes(from || '');
  const isFromCancelSelect = ['character2video', 'reference2image'].includes(from || '');

  // 按钮显示条件
  const showCancelSelect = isFromCancelSelect;
  const showSubmitButton =
    !isTeamSpace &&
    !isSubmitForbidden &&
    (mode === 'post' || (!isPreset && isPersonalUnsubmitted && mode === 'view' && !!onSubmitToCommunity));
  const showEdit = !isPreset && isPersonalUnsubmitted && mode === 'view' && !!onEdit;
  const showCollect = canCollect && !isPreset && (isSharedApprovedAuthor || isSharedNotAuthor) && mode === 'view';
  const showCancelSubmit = !isPreset && isShareAuthor && mode === 'view';
  const showLike = !isPreset && (isSharedApprovedAuthor || (isSharedNotAuthor && isSubmitSuccess)) && mode === 'view';
  const showCopy = !isPreset && isShareAuthor && mode === 'view';
  const showUse = !isFromSelect && mode === 'view';

  // 特殊处理：post_reference 来源只显示关闭按钮
  if (from === 'post_reference') {
    return (
      <div className={cn('flex h-10 items-center gap-2', className)}>
        <Button
          size="default"
          variant="outline"
          className="h-full flex-1 rounded-8 border-none bg-system-neutral02"
          onClick={handleClose}
        >
          {t('elements.elementDialog.close')}
        </Button>
      </div>
    );
  }

  const iconButtonClassName =
    'flex size-10 cursor-pointer items-center justify-center rounded-12 border-none bg-system-white04 hover:bg-system-white12';

  return (
    <div className={cn('flex h-10 items-center gap-4', className)}>
      {/* 取消选择 */}
      {showCancelSelect && (
        <Button
          variant="outline"
          className="hidden h-full rounded-8 border-none bg-system-neutral02 px-4 sm:block"
          onClick={onCancelSelect}
        >
          {t('elements.elementDialog.cancelSelect')}
        </Button>
      )}

      {/* 投稿 */}
      {showSubmitButton &&
        (mode === 'post' ? (
          <Button
            size="default"
            variant="outline"
            className="group flex h-full flex-1 items-center justify-center gap-2 rounded-8 border-none bg-white px-4 text-system-black disabled:bg-system-neutral02 disabled:text-white/40"
            disabled={disableSubmit}
            onClick={onSubmit}
          >
            <BaseLineSubmit3 className="size-5 text-system-black group-disabled:text-white/40" />
            <span>{t('elements.submit.dialog.form.submit')}</span>
          </Button>
        ) : (
          <CustomTooltip content={t('elements.submit.dialog.form.submit')} side="top" trigger="auto">
            <div className={iconButtonClassName} onClick={onSubmitToCommunity}>
              <BaseLineSubmit3 className="size-5 text-system-white" />
            </div>
          </CustomTooltip>
        ))}

      {/* 取消投稿 */}
      {showCancelSubmit && (
        <CustomTooltip content={t('elements.submit.dialog.form.cancelSubmit')} side="top" trigger="auto">
          <div className={iconButtonClassName} onClick={onCancel}>
            <BaseLineCancelsubmission className="size-5 text-system-white" />
          </div>
        </CustomTooltip>
      )}

      {/* 收藏 */}
      {showCollect && (
        <CustomTooltip side="top" content={t('profile.collect')}>
          <div className={iconButtonClassName}>
            <CollectBtn
              id={shareElementId || ''}
              tabType={CommunityEnum.PostReferences}
              itemCollect={reactInfo?.collected ?? false}
              itemCollectCount={reactInfo?.collect_count || 0}
              handleCollect={handleCollect}
              handleUnCollect={handleUnCollect}
              hideEmptyCollectCount={false}
              hideCount={true}
              className="size-full"
              iconClassName="size-5 sm:text-base"
            />
          </div>
        </CustomTooltip>
      )}

      {/* 点赞 */}
      {showLike && (
        <LikeButton
          size="default"
          variant="outline"
          className="h-full rounded-12 border-none bg-system-white04 hover:bg-system-white12"
          id={shareElementId || ''}
          itemVote={reactInfo?.voted || false}
          itemVoteCount={reactInfo?.vote_count || 0}
          tabType={CommunityEnum.PostReferences}
          handleVote={updateVote}
          handleUnVote={updateUnVote}
        />
      )}

      {/* 复制 */}
      {showCopy && (
        <CustomTooltip content={t('elements.submit.dialog.form.copy')} side="top" trigger="auto">
          <div className={iconButtonClassName} onClick={onCopy}>
            <BaseLineCopy className="size-5 text-system-white" />
          </div>
        </CustomTooltip>
      )}

      {/* 编辑 */}
      {showEdit && (
        <CustomTooltip content={t('elements.elementDialog.edit')} side="top" trigger="auto">
          <div className={iconButtonClassName} onClick={onEdit}>
            <BaseLineEdit className="size-5 text-system-white" />
          </div>
        </CustomTooltip>
      )}

      {/* 使用 */}
      {showUse && (
        <Button
          size="default"
          className="relative h-full flex-1 overflow-hidden rounded-12 bg-white text-black"
          onClick={onUse}
        >
          {t('elements.submit.dialog.form.use')}
          {isCommercial && (
            <div className="absolute top-0 right-0 flex h-[17px] items-center justify-center rounded-bl-8 bg-[#5765F2] px-1.5 font-medium text-[9px] text-system-white">
              {t('elements.limitedFree')}
            </div>
          )}
        </Button>
      )}
    </div>
  );
};
